import React, { useMemo } from 'react';
import {
    compose,
    defaultProps,
    mapProps,
    onlyUpdateForKeys,
    shouldUpdate,
    withHandlers,
    withProps,
    withState
} from 'recompose';
import { difference, equals, head, isNil, omit, path, pick, pipe, prop, unless } from 'ramda';
import { usePrevious } from 'js/hooks/usePrevious';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';
import { DISPLAY_MODE, GAME_SIZES } from 'js/components/common/GamesView/constants';
import { usePortletConfig, usePortletSelector } from 'js/components/system/Portlet';

import GamesView from '../GamesView';
import { CategoryContext } from '../context';
import { CATEGORY_TYPE } from '../constants';
import { ViewMoreWithExpanded } from './ViewMore';
import { trackGameCategoryActiveSlideChange } from '../trackingApi';

import style from './index.scss';

export const CategoryBare = ({
    gameCodes,
    setCategoryRef,
    displayMode,
    gameSize,
    expanded,
    scrollIntoView,
    className = '',
    displayName,
    showTitle,
    slidesLength,
    setSlidesLength,
    onActiveSlideChange,
    mostPlayedGame,
    t,
    ...category
}) => {
    const { cid, type } = category;
    const isPinned = type === CATEGORY_TYPE.PINNED;
    const { showViewMore } = usePortletConfig();
    const categoryClsName = [
        className,
        style.category,
        type === CATEGORY_TYPE.TAB ? style.tab : '',
        isPinned ? style.pinned : '',
        expanded ? style.expanded : ''
    ].join(' ');

    const previousGamesCodes = usePrevious(gameCodes);
    const lastAddedGameCode = useMemo(
        () =>
            unless(
                isNil,
                pipe(
                    difference(gameCodes),
                    head
                )
            )(previousGamesCodes),
        [gameCodes, previousGamesCodes]
    );

    return (
        <CategoryContext.Provider value={{ ...category, gameSize, lastAddedGameCode }}>
            <div ref={setCategoryRef} className={categoryClsName}>
                {showTitle && (
                    <h3 className={style.heading}>{`${t(displayName, mostPlayedGame && [mostPlayedGame])}`}
                        {showViewMore && slidesLength > 1 && (
                            <ViewMoreWithExpanded
                                t={t}
                                cid={cid}
                                expanded={expanded}
                            />
                        )}
                    </h3>
                )}

                <GamesView
                    displayMode={displayMode}
                    gameSize={gameSize}
                    gameCodes={gameCodes}
                    scrollIntoView={scrollIntoView}
                    setSlidesLength={setSlidesLength}
                    onActiveSlideChange={onActiveSlideChange}
                    expanded={expanded}
                    showDivider={isPinned}
                    isBulletsClickable={false}
                />
            </div>
        </CategoryContext.Provider>
    );
};

export const Category = compose(
    onlyUpdateForKeys(['type', 'gameCodes']),
    defaultProps({ showTitle: true }),
    withTranslations,
    withState('slidesLength', 'setSlidesLength', 0),
    withProps(() => useAppSelector(pick(['headerHeight']))),
    withHandlers(() => {
        let categoryRef;

        return {
            setCategoryRef: () => ref => categoryRef = ref,
            scrollIntoView: ({ headerHeight }) => () => {
                const { documentElement, body } = document;
                const { top: categoryTop } = categoryRef.getBoundingClientRect();
                const top = categoryTop + (documentElement.scrollTop || body.scrollTop) - headerHeight;

                window.scrollTo(0, top);
            }
        };
    }),
    withProps(({ mostPlayedGameCode }) => ({
        mostPlayedGame: useAppSelector(path(['games', mostPlayedGameCode, 'name'])),
        reactPortletId: prop('reactPortletId', usePortletConfig())
    })),
    mapProps(({ type, cid, ...other }) => {
        const isDesktop = useDeviceSelector(prop('isDesktop'));
        const { expandedCategory, feedName: gameFeed } = usePortletSelector(pick(['expandedCategory', 'feedName']));
        const expanded = expandedCategory === cid;

        const displayMode = expanded || !isDesktop && type === CATEGORY_TYPE.TAB
            ? DISPLAY_MODE.GRID
            : isDesktop
                ? DISPLAY_MODE.PAGING
                : DISPLAY_MODE.SLIDER;
        const gameSize = type === CATEGORY_TYPE.PINNED
            ? GAME_SIZES.LARGE
            : !isDesktop && (type === CATEGORY_TYPE.FAVORITES || type === CATEGORY_TYPE.RECENTLY)
                ? GAME_SIZES.SMALL
                : GAME_SIZES.MEDIUM;

        return {
            ...other,
            cid,
            type,
            expanded,
            gameFeed,
            displayMode,
            gameSize
        };
    }),
    withHandlers(() => ({
        onActiveSlideChange: ({ reactPortletId, gameFeed, name }) => ({ direction }) => {
            const elementClicked = direction === 'left' ? 'previous' : 'next';

            trackGameCategoryActiveSlideChange(reactPortletId, gameFeed, name, elementClicked);
        }
    })),
    shouldUpdate((props, nextProps) => {
        const isNotEqualExpanded = props.expanded !== nextProps.expanded;
        const isNotEqualGameCodes = !equals(props.gameCodes, nextProps.gameCodes);
        const isNotEqualDisplayMode = props.displayMode !== nextProps.displayMode;
        const isNotEqualSlidesLength = props.slidesLength !== nextProps.slidesLength;

        return isNotEqualExpanded || isNotEqualGameCodes || isNotEqualDisplayMode || isNotEqualSlidesLength;
    }),
    mapProps(omit(['gameFeed']))
)(CategoryBare);
